/**
 * DD-SUBSTRATE-RESOLVE-001 G2 (F-057) — Product Review is the "product checked before
 * Sales" step: a New Product line must carry a Base Substrate by then. The server refuses
 * `product_review/complete` without one (422 `product_review_substrate_missing` + station);
 * this client check is the early cue so the reviewer is sent to the picker instead of
 * watching an optimistic complete roll back.
 */
import type { Request } from '@/lib/dd-queue/types'
import type { ResolvedSubject } from '@/lib/dd-queue/resolveSubject'
import { isNpTriageCompositionScope } from '@/lib/dd-queue/pmaTriage'
import { isBuildResolved } from '@/lib/dd-queue/buildTbdFlag'
import { partLabel, partsMissingSubstrate } from '@/lib/dd-queue/lineParts'

/** Mirrors DdWorkflowMutationService::PRODUCT_REVIEW_SUBSTRATE_MISSING_MESSAGE. */
export const PRODUCT_REVIEW_NEEDS_SUBSTRATE =
  'Product Review cannot be completed: Base Substrate is not set on this line. ' +
  'Pick the substrate in Attributes (Full Request) or Edit Line, then complete Product Review.'

export const PRODUCT_REVIEW_GATE_CODE = 'product_review_substrate_missing'

/**
 * F-120 — with a parts list the parts decide (mirrors
 * DdWorkflowMutationService::PRODUCT_REVIEW_PART_SUBSTRATE_MISSING_MESSAGE).
 */
export function productReviewGateMessage(resolved: ResolvedSubject): string {
  const parts = resolved.parts ?? []
  if (parts.length === 0) return PRODUCT_REVIEW_NEEDS_SUBSTRATE
  const missing = new Set(partsMissingSubstrate(parts))
  const labels = parts.filter((p) => missing.has(p.ordinal)).map(partLabel)
  return (
    `Product Review cannot be completed: no substrate on ${labels.join(', ')} (Manufactured – In House). ` +
    'Pick the substrate on the part (Attributes → Parts, or Edit Line), ' +
    "or set the part's sourcing to hardware / vended, then complete Product Review."
  )
}

/** True when completing Product Review on this line would be refused for a missing substrate. */
export function productReviewBlockedBySubstrate(req: Request, resolved: ResolvedSubject): boolean {
  return isNpTriageCompositionScope(req, resolved) && !isBuildResolved(resolved.attributes, resolved.parts)
}
